import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export { default as Item } from './Item.vue';
export { default as ItemActions } from './ItemActions.vue';
export { default as ItemContent } from './ItemContent.vue';
export { default as ItemDescription } from './ItemDescription.vue';
export { default as ItemFooter } from './ItemFooter.vue';
export { default as ItemGroup } from './ItemGroup.vue';
export { default as ItemHeader } from './ItemHeader.vue';
export { default as ItemMedia } from './ItemMedia.vue';
export { default as ItemSeparator } from './ItemSeparator.vue';
export { default as ItemTitle } from './ItemTitle.vue';

const ItemVariantsBase = [
    'group/item',
    'flex',
    'items-center',
    'border-transparent',
    'rounded-2xl',
    'transition-colors',
    'duration-100',
    'flex-wrap',
    'outline-none',
    'focus-visible:border-ring',
    'focus-visible:ring-border-highlighted',
    'focus-visible:ring-[2px]',
    'bg-background-primary',
].join(' ');

export const itemVariants = cva(ItemVariantsBase, {
    variants: {
        variant: {
            default: 'bg-transparent',
            outline: 'border border-border-subdued bg-background-primary',
            muted: 'bg-background-primary/50',
        },
        size: {
            default: 'p-[16px] gap-4',
            sm: 'py-[12px] px-[16px] gap-2.5',
            lg: 'p-5 gap-4',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});

const MediaVariantsBase = [
    'flex shrink-0',
    'items-center',
    'justify-center',
    'gap-2',
    'group-has-[[data-slot=item-description]]/item:self-start',
    '[&_svg]:pointer-events-none',
    'group-has-[[data-slot=item-description]]/item:translate-y-0.5',
].join(' ');

export const itemMediaVariants = cva(MediaVariantsBase, {
    variants: {
        variant: {
            default: 'bg-transparent',
            icon: 'border rounded-sm bg-muted',
            image: 'size-16 rounded-sm overflow-hidden [&_img]:size-full [&_img]:object-cover',
        },
        size: {
            sm: '',
            md: '',
            lg: '',
        },
    },
    compoundVariants: [
        {
            variant: 'icon',
            size: 'sm',
            class: "size-6 [&_svg:not([class*='size-'])]:size-4",
        },
        {
            variant: 'icon',
            size: 'md',
            class: "size-8 [&_svg:not([class*='size-'])]:size-5",
        },
        {
            variant: 'icon',
            size: 'lg',
            class: "size-10 [&_svg:not([class*='size-'])]:size-6",
        },
    ],
    defaultVariants: {
        variant: 'default',
        size: 'md',
    },
});

export type ItemVariants = VariantProps<typeof itemVariants>;
export type ItemMediaVariants = VariantProps<typeof itemMediaVariants>;
